

import { UserPermissions } from '../enums/UserPermissions';
import { UserRole, BankRoles, ProviderRoles } from '../enums/UserRoles';

export interface RoutePermission {
  path: string;
  permissions?: UserPermissions[];
  roles?: UserRole[];
  requireAll?: boolean;
  description?: string;
}

export const ROUTE_PERMISSIONS: RoutePermission[] = [
  {
    path: '/login',
    description: 'Página de login - pública'
  },
  {
    path: '/recover-password',
    description: 'Recuperação de senha - pública'
  },
  {
    path: '/reset-password/:token',
    description: 'Reset de senha - pública'
  },
  {
    path: '/define-password/:token',
    description: 'Definição de senha (primeiro acesso) - pública'
  },

  {
    path: '/',
    description: 'Dashboard principal - todos os usuários autenticados'
  },

  {
    path: '/bank-register',
    permissions: [UserPermissions.users_create_bank],
    roles: [BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER],
    description: 'Cadastro de usuários do banco'
  },
  {
    path: '/provider-register',
    permissions: [UserPermissions.users_create_provider],
    roles: [ProviderRoles.MASTER_PROVIDER],
    description: 'Cadastro de usuários da prestadora'
  },
  {
    path: '/inspector-register',
    permissions: [UserPermissions.inspectors_create_provider, UserPermissions.inspectors_create_bank],
    roles: [BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER, ProviderRoles.MASTER_PROVIDER],
    description: 'Cadastro de inspetores'
  },

  {
    path: '/vendor-register',
    permissions: [UserPermissions.providers_create_bank],
    roles: [BankRoles.ADMIN_BANK],
    description: 'Cadastro de prestadoras'
  },

  {
    path: '/request-inspection',
    permissions: [UserPermissions.inspections_create_bank],
    roles: [BankRoles.ADMIN_BANK, BankRoles.BANK_OPERATIONAL],
    description: 'Solicitação de inspeção'
  },

  {
    path: '/checklists',
    permissions: [
      UserPermissions.checklist_read_inspector,
      UserPermissions.checklist_read_provider,
      UserPermissions.checklist_create_bank,
      UserPermissions.checklist_read_bank
    ],
    description: 'Listagem de checklists'
  },
  {
    path: '/checklist/new',
    permissions: [UserPermissions.checklist_create_bank],
    roles: [BankRoles.ADMIN_BANK],
    description: 'Criar novo checklist'
  },
  {
    path: '/checklist/edit/:id',
    permissions: [UserPermissions.checklist_update_bank],
    roles: [BankRoles.ADMIN_BANK],
    description: 'Editar checklist'
  }
];

export function findRoutePermission(path: string): RoutePermission | undefined {
  let routeConfig = ROUTE_PERMISSIONS.find(route => route.path === path);
  
  if (!routeConfig) {
    routeConfig = ROUTE_PERMISSIONS.find(route => {
      if (!route.path.includes(':')) return false;
      
      const routePattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      return regex.test(path);
    });
  }
  
  return routeConfig;
}

export function isPublicRoute(path: string): boolean {
  const publicRoutes = ['/login', '/recover-password', '/guest/video-call'];
  const resetPasswordPattern = /^\/reset-password\/[^/]+$/;
  const definePasswordPattern = /^\/define-password\/[^/]+$/;

  return publicRoutes.includes(path) || resetPasswordPattern.test(path) || definePasswordPattern.test(path);
}

export function routeRequiresPermissions(path: string): boolean {
  const routeConfig = findRoutePermission(path);
  return !!(routeConfig?.permissions?.length || routeConfig?.roles?.length);
}

export function getRoutePermissions(path: string): {
  permissions: UserPermissions[];
  roles: UserRole[];
  requireAll: boolean;
} {
  const routeConfig = findRoutePermission(path);
  
  return {
    permissions: routeConfig?.permissions || [],
    roles: routeConfig?.roles || [],
    requireAll: routeConfig?.requireAll || false
  };
}